import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Block } from '../../../shared/types'
import { unifiedApi } from '../../api/unifiedApi'

/**
 * 规范化块数据的日期字段
 * Electric/TanStack Collection 运行时返回的 createdAt/updatedAt 是 Date 对象
 * （类型声明 string 与实际不符），直入 Redux state 会触发 serializable 检查告警
 */
function normalizeBlockDates(block: Block): Block {
  if (!block) return block
  const { createdAt, updatedAt, ...rest } = block as any
  return {
    ...rest,
    ...(createdAt !== undefined && { createdAt: createdAt instanceof Date ? createdAt.toISOString() : createdAt }),
    ...(updatedAt !== undefined && { updatedAt: updatedAt instanceof Date ? updatedAt.toISOString() : updatedAt }),
  } as Block
}

export interface BlockCacheState {
  // 块数据缓存
  blocks: {
    [blockId: string]: {
      data: Block | null
      loading: boolean
      error: string | null
      lastUpdated: number
      subscriptionActive: boolean
    }
  }
  // 批量块数据缓存
  batchBlocks: {
    [cacheKey: string]: {
      blocks: Block[]
      loading: boolean
      error: string | null
      lastUpdated: number
    }
  }
  // 订阅状态
  subscriptions: {
    activeBlockIds: string[]
    lastSubscriptionUpdate: number
  }
  // 缓存配置
  config: {
    maxCacheSize: number
    cacheDuration: number // 缓存有效期（毫秒）
  }
}

const initialState: BlockCacheState = {
  blocks: {},
  batchBlocks: {},
  subscriptions: {
    activeBlockIds: [],
    lastSubscriptionUpdate: 0
  },
  config: {
    maxCacheSize: 1000, // 最多缓存1000个块
    cacheDuration: 5 * 60 * 1000 // 5分钟缓存
  }
}

// Async thunks

/**
 * 获取单个块数据
 */
export const fetchBlock = createAsyncThunk(
  'blockCache/fetchBlock',
  async (blockId: string, { getState }) => {
    try {
      const state = getState() as { blockCache: BlockCacheState }
      const cachedBlock = state.blockCache.blocks[blockId]
      
      // 检查缓存是否有效
      if (cachedBlock && 
          cachedBlock.data && 
          Date.now() - cachedBlock.lastUpdated < state.blockCache.config.cacheDuration) {
        return cachedBlock.data
      }
      
      // 使用 unifiedApi 获取块数据，支持 Tauri 环境
      const block = await unifiedApi.getBlockById(blockId)
      
      if (!block) {
        throw new Error(`块不存在: ${blockId}`)
      }
      
      return block
    } catch (error) {
      console.error(`💥 [blockCache] 获取块 ${blockId} 失败:`, error)
      throw error
    }
  }
)

/**
 * 批量获取块数据
 */
export const fetchBlocksBatch = createAsyncThunk(
  'blockCache/fetchBlocksBatch',
  async (blockIds: string[], { getState }) => {
    try {
      const state = getState() as { blockCache: BlockCacheState }
      const cacheKey = blockIds.sort().join('-')
      const cachedBatch = state.blockCache.batchBlocks[cacheKey]
      
      // 检查缓存是否有效
      if (cachedBatch && 
          Date.now() - cachedBatch.lastUpdated < state.blockCache.config.cacheDuration) {
        return cachedBatch.blocks
      }
      
      // 使用 unifiedApi 批量获取块数据
      const blocks: Block[] = []
      for (const id of blockIds) {
        const block = await unifiedApi.getBlockById(id)
        if (block) {
          blocks.push(block)
        }
      }
      
      return blocks
    } catch (error) {
      console.error(`💥 [blockCache] 批量获取块数据失败:`, error)
      throw error
    }
  }
)

const blockCacheSlice = createSlice({
  name: 'blockCache',
  initialState,
  reducers: {
    /**
     * 更新块数据（用于订阅更新）
     */
    updateBlock: (state, action: PayloadAction<Block>) => {
      const block = normalizeBlockDates(action.payload)
      state.blocks[block.id] = {
        data: block,
        loading: false,
        error: null,
        lastUpdated: Date.now(),
        subscriptionActive: true
      }
    },
    
    /**
     * 更新块缓存（通用更新方法）
     */
    updateBlockCache: (state, action: PayloadAction<{ id: string; data: any; lastUpdated: number }>) => {
      const { id, data, lastUpdated } = action.payload
      const normalized = data ? normalizeBlockDates(data) : data
      if (!state.blocks[id]) {
        state.blocks[id] = {
          data: normalized,
          loading: false,
          error: null,
          lastUpdated,
          subscriptionActive: false
        }
      } else {
        state.blocks[id].data = normalized
        state.blocks[id].lastUpdated = lastUpdated
        state.blocks[id].loading = false
        state.blocks[id].error = null
      }
    },
    
    /**
     * 设置块加载状态
     */
    setBlockLoading: (state, action: PayloadAction<{ blockId: string; loading: boolean }>) => {
      const { blockId, loading } = action.payload
      if (!state.blocks[blockId]) {
        state.blocks[blockId] = {
          data: null,
          loading,
          error: null,
          lastUpdated: 0,
          subscriptionActive: false
        }
      } else {
        state.blocks[blockId].loading = loading
      }
    },
    
    /**
     * 设置块错误状态
     */
    setBlockError: (state, action: PayloadAction<{ blockId: string; error: string }>) => {
      const { blockId, error } = action.payload
      if (!state.blocks[blockId]) {
        state.blocks[blockId] = {
          data: null,
          loading: false,
          error,
          lastUpdated: 0,
          subscriptionActive: false
        }
      } else {
        state.blocks[blockId].error = error
        state.blocks[blockId].loading = false
      }
    },
    
    /**
     * 清除块错误
     */
    clearBlockError: (state, action: PayloadAction<string>) => {
      const blockId = action.payload
      if (state.blocks[blockId]) {
        state.blocks[blockId].error = null
      }
    },
    
    /**
     * 设置订阅状态
     */
    setSubscriptionActive: (state, action: PayloadAction<{ blockId: string; active: boolean }>) => {
      const { blockId, active } = action.payload
      if (!state.blocks[blockId]) {
        state.blocks[blockId] = {
          data: null,
          loading: false,
          error: null,
          lastUpdated: 0,
          subscriptionActive: active
        }
      } else {
        state.blocks[blockId].subscriptionActive = active
      }
      
      // 更新活跃订阅列表
      if (active) {
        if (!state.subscriptions.activeBlockIds.includes(blockId)) {
          state.subscriptions.activeBlockIds.push(blockId)
        }
      } else {
        state.subscriptions.activeBlockIds = state.subscriptions.activeBlockIds.filter(id => id !== blockId)
      }
      state.subscriptions.lastSubscriptionUpdate = Date.now()
    },
    
    /**
     * 清除所有订阅
     */
    clearAllSubscriptions: (state) => {
      Object.keys(state.blocks).forEach(blockId => {
        state.blocks[blockId].subscriptionActive = false
      })
      state.subscriptions.activeBlockIds = []
      state.subscriptions.lastSubscriptionUpdate = Date.now()
    },
    
    /**
     * 清除块缓存
     */
    clearBlockCache: (state, action: PayloadAction<string>) => {
      const blockId = action.payload
      delete state.blocks[blockId]
    },
    
    /**
     * 清除所有缓存
     */
    clearAllCache: (state) => {
      state.blocks = {}
      state.batchBlocks = {}
      state.subscriptions.activeBlockIds = []
      state.subscriptions.lastSubscriptionUpdate = Date.now()
    },
    
    /**
     * 清理过期缓存
     */
    cleanupExpiredCache: (state) => {
      const now = Date.now()
      const cacheDuration = state.config.cacheDuration
      
      // 清理单个块缓存
      Object.keys(state.blocks).forEach(blockId => {
        const block = state.blocks[blockId]
        if (now - block.lastUpdated > cacheDuration && !block.subscriptionActive) {
          delete state.blocks[blockId]
        }
      })
      
      // 清理批量块缓存
      Object.keys(state.batchBlocks).forEach(cacheKey => {
        const batch = state.batchBlocks[cacheKey]
        if (now - batch.lastUpdated > cacheDuration) {
          delete state.batchBlocks[cacheKey]
        }
      })
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchBlock
      .addCase(fetchBlock.pending, (state, action) => {
        const blockId = action.meta.arg
        state.blocks[blockId] = {
          data: null,
          loading: true,
          error: null,
          lastUpdated: 0,
          subscriptionActive: false
        }
      })
      .addCase(fetchBlock.fulfilled, (state, action) => {
        const blockId = action.meta.arg
        if (state.blocks[blockId]) {
          state.blocks[blockId].data = normalizeBlockDates(action.payload)
          state.blocks[blockId].loading = false
          state.blocks[blockId].lastUpdated = Date.now()
        }
      })
      .addCase(fetchBlock.rejected, (state, action) => {
        const blockId = action.meta.arg
        if (state.blocks[blockId]) {
          state.blocks[blockId].loading = false
          state.blocks[blockId].error = action.error.message || '获取块数据失败'
        }
      })
      
      // fetchBlocksBatch
      .addCase(fetchBlocksBatch.pending, (state, action) => {
        const blockIds = action.meta.arg
        const cacheKey = blockIds.sort().join('-')
        state.batchBlocks[cacheKey] = {
          blocks: [],
          loading: true,
          error: null,
          lastUpdated: 0
        }
      })
      .addCase(fetchBlocksBatch.fulfilled, (state, action) => {
        const blockIds = action.meta.arg
        const cacheKey = blockIds.sort().join('-')
        if (state.batchBlocks[cacheKey]) {
          state.batchBlocks[cacheKey].blocks = (action.payload || []).map(normalizeBlockDates)
          state.batchBlocks[cacheKey].loading = false
          state.batchBlocks[cacheKey].lastUpdated = Date.now()
        }
      })
      .addCase(fetchBlocksBatch.rejected, (state, action) => {
        const blockIds = action.meta.arg
        const cacheKey = blockIds.sort().join('-')
        if (state.batchBlocks[cacheKey]) {
          state.batchBlocks[cacheKey].loading = false
          state.batchBlocks[cacheKey].error = action.error.message || '批量获取块数据失败'
        }
      })
  }
})

export const {
  updateBlock,
  updateBlockCache,
  setBlockLoading,
  setBlockError,
  clearBlockError,
  setSubscriptionActive,
  clearAllSubscriptions,
  clearBlockCache,
  clearAllCache,
  cleanupExpiredCache
} = blockCacheSlice.actions

export default blockCacheSlice.reducer
